import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./IntroAnimation.css";
import Logo from "../../assets/images/VR-Builder-logo-img.jpeg";

const IntroAnimation = ({ onComplete }) => {
  const [stage, setStage] = useState("blueprint"); // blueprint, construction, complete
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const constructionTimer = setTimeout(() => setStage("construction"), 2500);
    const logoTimer = setTimeout(() => setStage("complete"), 4500);
    const exitTimer = setTimeout(() => setIsExiting(true), 7500);
    const finishTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 8500);

    return () => {
      clearTimeout(constructionTimer);
      clearTimeout(logoTimer);
      clearTimeout(exitTimer);
      clearTimeout(finishTimer);
    };
  }, [onComplete]);

  // Framer Motion Variants
  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 2, ease: "easeInOut" },
    },
  };

  const fillVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1, ease: "easeOut" } },
  };

  return (
    <motion.div
      className="intro-overlay"
      initial={{ opacity: 1 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{ duration: 1 }}
    >
      <div className="grid-background"></div>

      <div className="construction-scene">
        <svg className="house-svg" viewBox="0 0 320 220">
          {/* --- LANDSCAPE --- */}
          {/* Grass Base */}
          <motion.rect
            x="0"
            y="200"
            width="320"
            height="20"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0 }}
          />
          <motion.rect
            x="0"
            y="200"
            width="320"
            height="20"
            className="grass-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Trees Left */}
          <motion.circle
            cx="40"
            cy="180"
            r="20"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.8 }}
          />
          <motion.circle
            cx="40"
            cy="180"
            r="20"
            className="tree-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.line
            x1="40"
            y1="200"
            x2="40"
            y2="210"
            stroke="#795548"
            strokeWidth="4"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Trees Right */}
          <motion.circle
            cx="280"
            cy="180"
            r="25"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.9 }}
          />
          <motion.circle
            cx="280"
            cy="180"
            r="25"
            className="tree-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.line
            x1="280"
            y1="205"
            x2="280"
            y2="215"
            stroke="#795548"
            strokeWidth="4"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* --- MAIN STRUCTURE --- */}
          {/* Yellow Box Walls */}
          <motion.rect
            x="70"
            y="120"
            width="180"
            height="80"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
          />
          <motion.rect
            x="70"
            y="120"
            width="180"
            height="80"
            className="wall-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Red Roof (Trapezoid) */}
          <motion.path
            d="M60,120 L100,60 L220,60 L260,120 Z"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.2 }}
          />
          <motion.path
            d="M60,120 L100,60 L220,60 L260,120 Z"
            className="roof-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          {/* Chimney */}
          <motion.rect
            x="200"
            y="40"
            width="15"
            height="40"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
          />
          <motion.rect
            x="200"
            y="40"
            width="15"
            height="40"
            fill="#D32F2F"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Dormer Windows (Left & Right) */}
          {/* Left Dormer Gable */}
          <motion.path
            d="M110,90 L125,70 L140,90 Z"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.4 }}
          />
          <motion.path
            d="M110,90 L125,70 L140,90 Z"
            className="wall-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.rect
            x="118"
            y="80"
            width="14"
            height="14"
            fill="#fff"
            stroke="none"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.rect
            x="120"
            y="82"
            width="10"
            height="10"
            className="window-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Right Dormer Gable */}
          <motion.path
            d="M180,90 L195,70 L210,90 Z"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.45 }}
          />
          <motion.path
            d="M180,90 L195,70 L210,90 Z"
            className="wall-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.rect
            x="188"
            y="80"
            width="14"
            height="14"
            fill="#fff"
            stroke="none"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.rect
            x="190"
            y="82"
            width="10"
            height="10"
            className="window-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Central Entrance (Protruding Gable) */}
          <motion.path
            d="M130,120 L160,95 L190,120 L190,200 L130,200 Z"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.6 }}
          />
          <motion.path
            d="M130,120 L160,95 L190,120 L190,200 L130,200 Z"
            className="wall-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Door */}
          <motion.rect
            x="145"
            y="150"
            width="30"
            height="50"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
          />
          <motion.rect
            x="145"
            y="150"
            width="30"
            height="50"
            className="door-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          {/* Door Window */}
          <motion.rect
            x="150"
            y="155"
            width="20"
            height="20"
            className="window-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Lower Windows (Left & Right) */}
          {/* Left Window */}
          <motion.rect
            x="80"
            y="150"
            width="40"
            height="30"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.7 }}
          />
          <motion.rect
            x="80"
            y="150"
            width="40"
            height="30"
            fill="#fff"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.rect
            x="83"
            y="153"
            width="34"
            height="24"
            className="window-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />

          {/* Right Window */}
          <motion.rect
            x="200"
            y="150"
            width="40"
            height="30"
            variants={pathVariants}
            initial="hidden"
            animate="visible"
            className="blueprint-stroke"
            transition={{ delay: 0.75 }}
          />
          <motion.rect
            x="200"
            y="150"
            width="40"
            height="30"
            fill="#fff"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
          <motion.rect
            x="203"
            y="153"
            width="34"
            height="24"
            className="window-fill"
            variants={fillVariants}
            initial="hidden"
            animate={
              stage === "construction" || stage === "complete"
                ? "visible"
                : "hidden"
            }
          />
        </svg>
      </div>

      <AnimatePresence>
        {stage === "complete" && (
          <motion.div
            className="final-logo-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <img
              src={Logo}
              alt="Vishnu Construction"
              className="full-screen-logo"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default IntroAnimation;
